import { useMutation } from "@apollo/client";
import pluralize from "pluralize";
import { GET_TIERII_REPORT } from "routes/Staff/Fulfillment/FulfillmentDetails/useReportDetails";
import { useAlerts } from "../../components/Alerts/AlertProvider";
import { gql } from "../../generated-graphql";
import { UpdateActivityMutation } from "../../generated-graphql/graphql";

const ACTIVITY_FRAGMENT = gql(`
  fragment ActivityFragment on Activity {
    id
    type
    status
    executionPlan
    description
    assigneeId
    assignee {
      id
      email
      person {
        id
        first
        last
      }
    }
    workflow {
      id
      tierIIReportId
    }
  }
`);

const UPDATE_ACTIVITY = gql(`
  mutation UpdateActivity($id: ID!, $input: ActivityInput!) {
    updateActivity(id: $id, input: $input) {
      ... ActivityFragment
    }
  }
`);

export const useUpdateActivity = (additionalRefetchQueries?: string[]) => {
  const alerts = useAlerts();
  const mutation = useMutation<UpdateActivityMutation>(UPDATE_ACTIVITY, {
    refetchQueries: [
      "TierIIReportActivities",
      ...(additionalRefetchQueries || []),
    ],
    onCompleted(data) {
      alerts.success(`Successfully updated Activity ${data.updateActivity.id}`);
    },
    onError(error) {
      console.error(error);
      alerts.error(`An error occurred while updated the Activity`, error);
    },
    optimisticResponse: (variables) => ({
      __typename: "Mutation",
      updateActivity: {
        __typename: "Activity",
        id: variables.id,
        ...variables.input,
      },
    }),
  });

  return mutation;
};

const UPDATE_ACTIVITIES = gql(`
  mutation UpdateActivities($ids: [ID!]!, $input: ActivityInput!, $allowPartialFailure: Boolean!) {
    updateActivities(ids: $ids, input: $input, allowPartialFailure: $allowPartialFailure) {
      updatedActivities {
        ... ActivityFragment
      }
      errors {
        id
        errorMessage
      }
    }
  }
`);

export const useUpdateActivities = () => {
  const alerts = useAlerts();
  const mutation = useMutation(UPDATE_ACTIVITIES, {
    refetchQueries: ["TierIIReportActivities", "TierIIReportActivitiesInError"],
    onCompleted(data) {
      const failedUpdates = data.updateActivities?.errors?.map(
        (error) => `id:${error?.id} ${error?.errorMessage}`
      );
      const failedUpdatesMessage = `Failed to update ${
        data.updateActivities?.errors?.length
      } activities
      ${failedUpdates?.join(", ")}`;
      alerts.success(
        `Successfully updated ${
          data.updateActivities?.updatedActivities?.length
        } ${pluralize(
          "activity",
          data.updateActivities?.updatedActivities?.length
        )}
        ${failedUpdates?.length ? failedUpdatesMessage : ""}
        `
      );
    },
    onError(error) {
      console.error(error);
      alerts.error(`An error occurred while updating activities`, error);
    },
  });

  return mutation;
};

const UPDATE_ACTIVITY_ASSIGNEE = gql(`
  mutation UpdateActivityAssignees($ids: [ID!]!, $assigneeId: ID) {
    updateActivityAssignees(ids: $ids, assigneeId: $assigneeId) {
      updatedActivities
    }
  }
`);

export const useUpdateActivityAssignees = () => {
  const alerts = useAlerts();
  const mutation = useMutation(UPDATE_ACTIVITY_ASSIGNEE, {
    refetchQueries: [
      "TierIIReportActivities",
      "TierIIReportActivitiesInError",
      "WorkflowActivities",
      "WorkflowOverviewStats",
      GET_TIERII_REPORT,
    ],
    awaitRefetchQueries: true,
    onCompleted(data) {
      alerts.success(
        `Successfully updated ${
          data.updateActivityAssignees?.updatedActivities
        } ${pluralize(
          "activity",
          data.updateActivityAssignees?.updatedActivities
        )}`
      );
    },
    onError(error) {
      console.error(error);
      alerts.error(`An error occurred while updating activities`, error);
    },
  });

  return mutation;
};
